import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { EMPTY } from 'rxjs';
import { delay, expand, first } from 'rxjs/operators';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { TablesTranslations } from '~shared/components/tables/tables-translations.service';
import { ModalService } from '~shared/components/elements/global-modal/modal.service';
import { ActionButton, ActionButtonEvent, Lottery, PriceCategory, PriceReduction, ProcessConfiguration } from '~core/models';
import { PriceCategoryService, PriceReductionService } from '~core/services';
import { TooltipTranslationService } from '~shared/translations';
import { ActionButtonType } from '~core/enum';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Handles price categories list.
 */
@UntilDestroy()
@Component({
  selector: 'app-price-categories-list',
  templateUrl: './price-categories-list.component.html',
})
export class PriceCategoriesListComponent implements OnInit, OnDestroy {
  public priceCategoriesList: Array<PriceCategory> = [];
  public columns: Array<string> = [];
  public actionButtons: Array<ActionButton> = [];
  public keys: Array<Array<string>> = [];
  public currentProcessConfiguration: ProcessConfiguration;
  public loading: boolean = false;
  public i18nDeleteTitle: string;
  public i18nDeleteParagraph: string;

  @Input() public selectedLottery: Lottery;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public tablesTranslations: TablesTranslations,
    private tixxSelectOverviewService: EventAdministrationService,
    private priceCategoryService: PriceCategoryService,
    private priceReductionService: PriceReductionService,
    public tooltipTranslations: TooltipTranslationService,
    private modalService: ModalService,
    public spinnerService: SpinnerService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.priceCategoryService.castCurrentPriceCategoriesList.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data) {
          this.priceCategoriesList = data;
        }
      });
    this.priceReductionService.castCurrentPriceReductionList.pipe(untilDestroyed(this))
      .subscribe((data: Array<PriceReduction>) => {
        if (data && this.selectedLottery) {
          this.priceCategoryService.get(this.selectedLottery).pipe(untilDestroyed(this))
            .subscribe(res => {
              this.priceCategoryService.setCurrentPriceCategoriesList(res);
            });
        }
      });
    this.columns.push(
      '',
      this.tablesTranslations.i18nName,
      this.tablesTranslations.i18nAmount,
      this.tablesTranslations.i18nAssignedReductions,
    );
    this.keys.push(['color', 'name', 'fullPrice', 'allReductions']);
  }

  public ngOnInit(): void {
    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.REDIRECT,
        icon: '#esf-icon-dollar-check',
        tooltip: this.tooltipTranslations.i18nEdit,
        disabled: !this.selectedLottery.isDraftMode
      }),
      new ActionButton({
        type: ActionButtonType.EDIT,
        icon: '#esf-icon-edit',
        tooltip: this.tooltipTranslations.i18nEdit,
        disabled: !this.selectedLottery.isDraftMode
      }),
      new ActionButton({
        type: ActionButtonType.DELETE,
        icon: '#esf-icon-trash',
        tooltip: this.tooltipTranslations.i18nDelete,
        disabled: !this.selectedLottery.isDraftMode
      })
    );
    this.i18nDeleteTitle = $localize`:@@tixxSelect.modalTitle.deletePriceCategory:Delete Price Category?`;
    this.i18nDeleteParagraph = $localize`:@@tixxSelect.modalParagraph.deletePriceCategory:When deleting a price category the information will be lost and the price cannot be booked anymore. Would you like to delete the price category?`;

    if (this.priceCategoryService.currentlyFetching.value) {
      this.fetchPricesLoop();
    } else {
      this.priceCategoryService.get(this.selectedLottery).pipe(untilDestroyed(this))
        .subscribe(res => {
          this.priceCategoryService.setCurrentPriceCategoriesList(res);
        });
    }
  }

  public ngOnDestroy(): void { }

  /**
   * Open the create price category modal
   */
  public fetchPriceCategories(): void {
    const obj = { lottery: this.selectedLottery['@id'] };
    this.priceCategoryService.fetchCategories(obj).pipe(first())
      .subscribe(res => {
        this.priceCategoryService.setCurrentlyFetching(res);
        this.fetchPricesLoop();
      });
  }

  /**
   * Open the edit price category modal
   */
  public editPriceCategory(priceCategory: any): any {
    this.priceCategoryService.setCurrentPriceCategory(priceCategory);
    this.tixxSelectOverviewService.toggleAllModal(false);
    this.tixxSelectOverviewService.toggleEditPriceCategoriesModal(true);
  }

  /**
   * Open the edit assigned reductions modal
   */
  public editAssignedReductions(priceCategory: any): any {
    this.priceCategoryService.setCurrentPriceCategory(priceCategory);
    this.tixxSelectOverviewService.toggleAllModal(false);
    this.tixxSelectOverviewService.toggleEditAssignedReductionsModal(true);
  }

  public async showDeleteModal(id: string): Promise<void> {
    this.modalService.setModalOptions(this.i18nDeleteTitle, this.i18nDeleteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.deletePriceCategory(id);
    }
  }

  public deletePriceCategory(id: string): void {
    this.priceCategoryService.delete(id).pipe(untilDestroyed(this))
      .subscribe(el => {
        this.priceCategoryService.deletePriceCategoryList(id);
        this.modalService.setStatus(false);
      });
  }

  public fetchPricesLoop(): any {
    this.loading = true;
    this.priceCategoryService.get(this.selectedLottery).pipe(
      untilDestroyed(this),
      delay(5000),
      expand(res => res.length === 0 ? this.priceCategoryService.get(this.selectedLottery)
        .pipe(delay(5000), untilDestroyed(this)) : EMPTY),
    ).subscribe(res => {
      if (res.length !== 0) {
        this.loading = false;
        this.priceCategoryService.setCurrentPriceCategoriesList(res);
        this.priceCategoryService.setCurrentlyFetching(false);
      }
    });

    this.priceReductionService.get(this.selectedLottery).pipe(
      untilDestroyed(this),
      delay(5000),
      expand(res => res.length === 0 ? this.priceReductionService.get(this.selectedLottery)
        .pipe(delay(5000), untilDestroyed(this)) : EMPTY),
    ).subscribe(res => {
      if (res.length !== 0) {
        this.priceReductionService.setCurrentPriceReductionList(res);
      }
    });
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<any>): void {
    switch (actionButton.type) {
      case ActionButtonType.REDIRECT:
        this.editAssignedReductions(actionButton.data);
        break;
      case ActionButtonType.EDIT:
        this.editPriceCategory(actionButton.data);
        break;
      case ActionButtonType.DELETE:
        this.showDeleteModal(actionButton.data.id);
        break;
      default:
    }
  }
}
